<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex gap-2 flex-wrap">
          <button
            type="button"
            class="btn btn-outline-primary"
            v-can="'Payroll.Edit'"
            @click="startCorporateStarter"
          >
            Corporate PAY starter
          </button>
          <button
            type="button"
            class="btn btn-primary"
            v-can="'Payroll.Edit'"
            @click="startCreate"
          >
            <i class="ti ti-plus me-1"></i>New bank format
          </button>
        </div>
      </div>

      <div class="row">
        <div class="col-lg-4">
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">Saved formats</h5>
            </div>
            <ul class="list-group list-group-flush">
              <li
                class="list-group-item list-group-item-action dq-template-item"
                :class="{ active: selectedId === 0 }"
                @click="selectLegacy"
              >
                <div class="fw-semibold dq-template-item__title">Standard (DoneQuick)</div>
                <div class="small dq-template-item__meta">Built-in CSV layout</div>
              </li>
              <li
                v-for="t in templates"
                :key="t.id"
                class="list-group-item list-group-item-action dq-template-item"
                :class="{ active: selectedId === t.id }"
                @click="selectTemplate(t)"
              >
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <div class="fw-semibold">{{ t.name }}</div>
                    <div class="small text-muted">
                      {{ (t.file_format || "csv").toUpperCase() }}
                      · {{ mappedColumnCount(t) }} mapped
                      <span v-if="t.filter_bank_code"> · bank {{ t.filter_bank_code }}</span>
                    </div>
                  </div>
                  <div class="d-flex flex-column gap-1 align-items-end">
                    <span v-if="t.is_default" class="badge bg-success">Default</span>
                  </div>
                </div>
              </li>
              <li
                v-if="!templates.length"
                class="list-group-item text-muted small"
              >
                No custom formats yet. Use New bank format or Corporate PAY starter.
              </li>
            </ul>
          </div>
        </div>

        <div class="col-lg-8">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0">{{ editorTitle }}</h5>
              <div v-if="selectedId > 0" class="d-flex gap-2">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  v-can="'Payroll.Edit'"
                  @click="duplicateTemplate"
                >
                  Duplicate
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  v-can="'Payroll.Edit'"
                  @click="removeTemplate"
                >
                  Delete
                </button>
              </div>
            </div>
            <div class="card-body">
              <div v-if="selectedId === 0">
                <p class="dq-standard-desc mb-0">
                  Built-in 12-column CSV used when you choose <strong>Standard (DoneQuick)</strong> on the Bank Transfer Report.
                </p>
                <button
                  type="button"
                  class="btn btn-outline-primary btn-sm"
                  v-can="'Payroll.Edit'"
                  @click="startCreate"
                >
                  Customize from standard…
                </button>
              </div>

              <form v-else @submit.prevent="save">
                <div class="row g-3 mb-3">
                  <div class="col-md-6">
                    <label class="form-label">Format name</label>
                    <input v-model="form.name" class="form-control" required />
                  </div>
                  <div class="col-md-3">
                    <label class="form-label">File type</label>
                    <select v-model="form.file_format" class="form-select">
                      <option value="csv">CSV</option>
                      <option value="xlsx">Excel (.xlsx)</option>
                    </select>
                  </div>
                  <div class="col-md-3 d-flex align-items-end">
                    <div class="form-check">
                      <input
                        id="bt-default"
                        v-model="form.is_default"
                        class="form-check-input"
                        type="checkbox"
                      />
                      <label class="form-check-label" for="bt-default">Default for exports</label>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Bank grid width (columns)</label>
                    <input
                      v-model.number="form.layout.sparse_column_count"
                      type="number"
                      min="0"
                      class="form-control"
                      placeholder="0 = compact file"
                    />
                    <div class="form-text">Use 355+ for corporate PAY templates with numbered header row.</div>
                  </div>
                  <div class="col-md-4 d-flex align-items-end">
                    <div class="form-check">
                      <input
                        id="bt-numbered"
                        v-model="form.layout.use_numbered_header"
                        class="form-check-input"
                        type="checkbox"
                      />
                      <label class="form-check-label" for="bt-numbered">Numbered header row (1, 2, 3…)</label>
                    </div>
                  </div>
                  <div class="col-12">
                    <div class="border rounded p-3 bg-light">
                      <h6 class="mb-2">Import from bank Excel</h6>
                      <div class="row g-2 align-items-end">
                        <div class="col-md-2">
                          <label class="form-label small">Header row #</label>
                          <input
                            v-model.number="importHeaderRow"
                            type="number"
                            min="1"
                            max="20"
                            class="form-control form-control-sm"
                          />
                        </div>
                        <div class="col-md-2">
                          <label class="form-label small">Number row #</label>
                          <input
                            v-model.number="importNumberRow"
                            type="number"
                            min="0"
                            max="20"
                            class="form-control form-control-sm"
                          />
                          <div class="form-text">0 = none</div>
                        </div>
                        <div class="col-md-5">
                          <label class="form-label small">Bank template file</label>
                          <input
                            type="file"
                            class="form-control form-control-sm"
                            accept=".xlsx,.xls,.csv"
                            @change="onImportFile"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Auto-select when bank code filter matches</label>
                    <input
                      v-model="form.filter_bank_code"
                      class="form-control"
                      placeholder="e.g. 01 — optional"
                    />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Payment / value date</label>
                    <select v-model="form.layout.payment_date_rule" class="form-select">
                      <option
                        v-for="r in paymentDateRules"
                        :key="r.value"
                        :value="r.value"
                      >
                        {{ r.label }}
                      </option>
                    </select>
                  </div>
                </div>

                <p class="small text-muted mb-2">
                  Account padding (e.g. 13 digits) is optional — add
                  <code>pad13</code> in Transforms only for banks that require it.
                </p>

                <div class="row g-2 align-items-end mb-2">
                  <div class="col-md-3">
                    <label class="form-label small mb-0">Preview payroll month</label>
                    <input v-model="previewPeriod" type="month" class="form-control form-control-sm" />
                  </div>
                  <div class="col-md-9 d-flex flex-wrap gap-2 justify-content-md-end align-items-center">
                    <div class="form-check mb-0">
                      <input
                        id="bt-mapped-only"
                        v-model="showMappedOnly"
                        class="form-check-input"
                        type="checkbox"
                      />
                      <label class="form-check-label small" for="bt-mapped-only">Mapped columns only</label>
                    </div>
                    <button
                      v-if="selectedId > 0"
                      type="button"
                      class="btn btn-sm btn-outline-secondary"
                      @click="runPreview"
                    >
                      Validate
                    </button>
                    <button
                      v-if="selectedId > 0"
                      type="button"
                      class="btn btn-sm btn-outline-secondary"
                      @click="downloadPreview"
                    >
                      Download sample
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-primary" @click="addColumn">
                      Add column
                    </button>
                  </div>
                </div>

                <div v-if="previewWarnings.length" class="alert alert-warning py-2 small mb-2">
                  <div v-for="(w, i) in previewWarnings" :key="i">{{ w.message }}</div>
                </div>

                <div class="table-responsive">
                  <table class="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th style="width: 55px">Sort</th>
                        <th style="width: 70px">Col #</th>
                        <th>Bank header</th>
                        <th>Source</th>
                        <th>Constant</th>
                        <th>Transforms</th>
                        <th style="width: 72px"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="idx in visibleColumnIndices" :key="idx">
                        <td>
                          <input
                            v-model.number="form.columns[idx].sort"
                            type="number"
                            min="1"
                            class="form-control form-control-sm"
                          />
                        </td>
                        <td>
                          <input
                            v-model.number="form.columns[idx].column_index"
                            type="number"
                            min="1"
                            class="form-control form-control-sm"
                          />
                        </td>
                        <td>
                          <input
                            v-model="form.columns[idx].header"
                            class="form-control form-control-sm"
                            required
                          />
                        </td>
                        <td>
                          <select
                            v-model="form.columns[idx].source"
                            class="form-select form-select-sm"
                          >
                            <option value="">— unmapped —</option>
                            <option v-for="s in sourceOptions" :key="s" :value="s">{{ s }}</option>
                          </select>
                        </td>
                        <td>
                          <input
                            v-if="form.columns[idx].source === 'constant'"
                            v-model="form.columns[idx].constant"
                            class="form-control form-control-sm"
                            placeholder="Constant value"
                          />
                          <input
                            v-else-if="form.columns[idx].source === 'formula'"
                            v-model="form.columns[idx].formula"
                            class="form-control form-control-sm"
                            placeholder="SALARY {period_label}"
                          />
                          <span v-else class="text-muted small">—</span>
                        </td>
                        <td>
                          <input
                            v-model="form.columns[idx].transformsText"
                            class="form-control form-control-sm"
                            placeholder="pad13, digits, dd/MM/yyyy"
                            @blur="applyTransformShortcut(form.columns[idx])"
                          />
                        </td>
                        <td class="text-nowrap">
                          <button
                            type="button"
                            class="btn btn-sm btn-light py-0"
                            title="Move up"
                            :disabled="idx === 0"
                            @click="moveColumn(idx, -1)"
                          >
                            <i class="ti ti-arrow-up"></i>
                          </button>
                          <button
                            type="button"
                            class="btn btn-sm btn-light py-0"
                            title="Move down"
                            :disabled="idx >= form.columns.length - 1"
                            @click="moveColumn(idx, 1)"
                          >
                            <i class="ti ti-arrow-down"></i>
                          </button>
                          <button
                            type="button"
                            class="btn btn-sm btn-light text-danger py-0"
                            @click="form.columns.splice(idx, 1)"
                          >
                            <i class="ti ti-trash"></i>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p class="small text-muted">
                  Transform shortcuts:
                  <code>pad13</code> (13-digit account),
                  <code>digits</code>,
                  <code>dd/MM/yyyy</code> on dates.
                </p>

                <button
                  type="submit"
                  class="btn btn-primary"
                  v-can="'Payroll.Edit'"
                  :disabled="saving"
                >
                  <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
                  Save format
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import moment from "moment";
import layoutHeader from "@/views/layouts/layout-header.vue";
import layoutSidebar from "@/views/layouts/layout-sidebar.vue";
import bankTransferTemplateService from "@/services/bankTransferTemplate.service.js";
import { triggerDownload } from "@/utils/bankTransferFileDownload.js";
import { toast } from "@/utils/toast";

const title = "Bank Transfer Formats";
const text = "Payroll";
const text1 = "Bank upload columns";

const templates = ref([]);
const sourceOptions = ref([]);
const legacyColumns = ref([]);
const corporateStarter = ref(null);
const paymentDateRules = ref([
  { value: "export_day", label: "Export day (today)" },
  { value: "period_end", label: "Payroll period end" },
  { value: "period_start", label: "Payroll period start" },
  { value: "payroll_payment_date", label: "Stored payment date on row" },
]);
const selectedId = ref(0);
const saving = ref(false);
const previewWarnings = ref([]);
const importHeaderRow = ref(2);
const importNumberRow = ref(1);
const showMappedOnly = ref(false);
const previewPeriod = ref(moment().format("YYYY-MM"));

const form = reactive({
  name: "",
  file_format: "csv",
  is_default: false,
  filter_bank_code: "",
  layout: {
    sparse_column_count: 0,
    use_numbered_header: false,
    sheet_title: "Bank upload",
    payment_date_rule: "export_day",
  },
  columns: [],
});

const editorTitle = computed(() =>
  selectedId.value === 0 ? "Standard (DoneQuick)" : form.name || "Edit format",
);

const visibleColumnIndices = computed(() => {
  const indices = form.columns.map((_, i) => i);
  if (!showMappedOnly.value) return indices;
  return indices.filter((i) => (form.columns[i]?.source || "").trim() !== "");
});

function mappedColumnCount(t) {
  return (t.columns || []).filter((c) => (c.source || "").trim() !== "").length;
}

function previewParams() {
  return {
    frequency: "monthly",
    period: previewPeriod.value,
    limit: 10,
  };
}

function moveColumn(index, direction) {
  const target = index + direction;
  if (target < 0 || target >= form.columns.length) return;
  const cols = form.columns;
  [cols[index], cols[target]] = [cols[target], cols[index]];
  cols.forEach((c, i) => {
    c.sort = i + 1;
  });
}

function cloneColumns(cols) {
  return (cols || []).map((c, i) => ({
    sort: c.sort ?? i + 1,
    column_index: c.column_index ?? c.sort ?? i + 1,
    header: c.header || "",
    source: c.source ?? "",
    constant: c.constant ?? "",
    formula: c.formula ?? "",
    transforms: Array.isArray(c.transforms) ? [...c.transforms] : [],
    transformsText: transformsToText(c.transforms),
  }));
}

function layoutFromApi(layout) {
  const l = layout || {};
  const headerRows = l.header_rows || [];
  return {
    sparse_column_count: l.sparse_column_count ?? 0,
    use_numbered_header: headerRows.some((r) => r.type === "column_numbers"),
    sheet_title: l.sheet_title || "Bank upload",
    payment_date_rule: l.payment_date_rule || "export_day",
  };
}

function layoutToPayload() {
  const header_rows = [];
  if (form.layout.use_numbered_header) {
    header_rows.push({ type: "column_numbers", start: 1 });
  }
  header_rows.push({ type: "labels" });
  return {
    sparse_column_count: form.layout.sparse_column_count || 0,
    sheet_title: form.layout.sheet_title || "Bank upload",
    payment_date_rule: form.layout.payment_date_rule || "export_day",
    header_rows,
  };
}

function transformsToText(transforms) {
  if (!Array.isArray(transforms) || !transforms.length) return "";
  const parts = [];
  for (const t of transforms) {
    if (t.type === "pad_left" && t.length === 13) parts.push("pad13");
    else if (t.type === "digits_only") parts.push("digits");
    else if (t.type === "date_format" && t.output === "dd/MM/yyyy") parts.push("dd/MM/yyyy");
  }
  return parts.join(" ");
}

function applyTransformShortcut(col) {
  const text = (col.transformsText || "").toLowerCase();
  const transforms = [];
  if (text.includes("digits")) transforms.push({ type: "digits_only" });
  if (text.includes("pad13")) {
    transforms.push({ type: "pad_left", length: 13, char: "0" });
  }
  if (text.includes("dd/mm/yyyy")) {
    transforms.push({ type: "date_format", output: "dd/MM/yyyy" });
  }
  col.transforms = transforms;
}

function selectLegacy() {
  selectedId.value = 0;
}

function selectTemplate(t) {
  selectedId.value = t.id;
  form.name = t.name;
  form.file_format = t.file_format || "csv";
  form.is_default = !!t.is_default;
  form.filter_bank_code = t.filter_bank_code || "";
  Object.assign(form.layout, layoutFromApi(t.layout));
  form.columns = cloneColumns(t.columns);
  previewWarnings.value = [];
}

function startCreate() {
  selectedId.value = -1;
  form.name = "My bank upload";
  form.file_format = "xlsx";
  form.is_default = false;
  form.layout.sparse_column_count = 0;
  form.layout.use_numbered_header = false;
  form.columns = cloneColumns(legacyColumns.value);
  previewWarnings.value = [];
}

function startCorporateStarter() {
  if (!corporateStarter.value) return;
  selectedId.value = -1;
  const s = corporateStarter.value;
  form.name = s.name;
  form.file_format = s.file_format || "xlsx";
  form.is_default = false;
  Object.assign(form.layout, layoutFromApi(s.layout));
  form.columns = cloneColumns(s.columns);
  previewWarnings.value = [];
}

function addColumn() {
  const n = form.columns.length + 1;
  form.columns.push({
    sort: n,
    column_index: n,
    header: "New column",
    source: "employee.employee_name",
    constant: "",
    transforms: [],
    transformsText: "",
  });
}

async function onImportFile(event) {
  const file = event.target.files?.[0];
  event.target.value = "";
  if (!file) return;
  try {
    const res = await bankTransferTemplateService.importHeaders(file, {
      headerRow: importHeaderRow.value,
      numberRow: importNumberRow.value,
    });
    const imp = res.data?.import;
    const suggested = res.data?.suggested_layout;
    if (suggested) {
      Object.assign(form.layout, layoutFromApi(suggested));
    }
    if (imp?.columns?.length) {
      selectedId.value = selectedId.value || -1;
      form.file_format = "xlsx";
      form.columns = cloneColumns(imp.columns);
      showMappedOnly.value = true;
      toast(`Imported ${imp.columns.length} headers — map sources, then hide unmapped if you like.`, "success");
    }
  } catch (e) {
    toast(e?.response?.data?.message || "Could not read bank file.", "error");
  }
}

async function downloadPreview() {
  if (selectedId.value <= 0) return;
  try {
    const fmt = form.file_format === "xlsx" ? "xlsx" : "csv";
    const res = await bankTransferTemplateService.previewExport(selectedId.value, {
      ...previewParams(),
      format: fmt,
    });
    const ext = fmt === "xlsx" ? "xlsx" : "csv";
    const blob = new Blob([res.data], {
      type:
        fmt === "xlsx"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "text/csv;charset=utf-8",
    });
    triggerDownload(blob, `bank-format-preview.${ext}`);
    toast("Sample file downloaded.", "success");
  } catch (e) {
    toast(e?.response?.data?.message || "Could not download sample.", "error");
  }
}

async function runPreview() {
  if (selectedId.value <= 0) return;
  try {
    const res = await bankTransferTemplateService.preview(
      selectedId.value,
      previewParams(),
    );
    previewWarnings.value = res.data?.warnings || [];
    if (!previewWarnings.value.length) {
      toast("Preview OK — no validation issues on sample rows.", "success");
    }
  } catch (e) {
    toast(e?.response?.data?.message || "Preview failed.", "error");
  }
}

async function load() {
  const [metaRes, listRes] = await Promise.all([
    bankTransferTemplateService.meta(),
    bankTransferTemplateService.list(),
  ]);
  sourceOptions.value = metaRes.data?.sources || [];
  legacyColumns.value = metaRes.data?.legacy?.columns || [];
  corporateStarter.value = metaRes.data?.starters?.corporate_pay || null;
  if (metaRes.data?.payment_date_rules?.length) {
    paymentDateRules.value = metaRes.data.payment_date_rules;
  }
  templates.value = (listRes.data?.templates || []).filter((t) => !t.system && t.id);
}

async function save() {
  if (selectedId.value <= 0) return;
  saving.value = true;
  try {
    form.columns.forEach((c) => applyTransformShortcut(c));
    const payload = {
      name: form.name,
      file_format: form.file_format,
      is_default: form.is_default,
      filter_bank_code: form.filter_bank_code?.trim() || null,
      layout: layoutToPayload(),
      columns: form.columns.map(({ transformsText, ...rest }) => ({
        ...rest,
        constant: rest.source === "constant" ? rest.constant : null,
        formula: rest.source === "formula" ? rest.formula : null,
      })),
    };

    if (selectedId.value === -1) {
      const res = await bankTransferTemplateService.create(payload);
      await load();
      selectTemplate(res.data.template);
      toast("Bank format created.", "success");
    } else {
      const res = await bankTransferTemplateService.update(selectedId.value, payload);
      await load();
      selectTemplate(res.data.template);
      toast("Bank format saved.", "success");
    }
  } catch (e) {
    toast(e?.response?.data?.message || "Could not save bank format.", "error");
  } finally {
    saving.value = false;
  }
}

async function duplicateTemplate() {
  if (selectedId.value <= 0) return;
  try {
    const res = await bankTransferTemplateService.duplicate(selectedId.value);
    await load();
    selectTemplate(res.data.template);
    toast("Format duplicated.", "success");
  } catch (e) {
    toast(e?.response?.data?.message || "Could not duplicate.", "error");
  }
}

async function removeTemplate() {
  if (selectedId.value <= 0) return;
  if (!window.confirm("Delete this bank transfer format?")) return;
  try {
    await bankTransferTemplateService.remove(selectedId.value);
    await load();
    selectLegacy();
    toast("Format deleted.", "success");
  } catch (e) {
    toast(e?.response?.data?.message || "Could not delete.", "error");
  }
}

onMounted(() => {
  load().catch((e) => {
    console.error("Bank transfer formats load failed:", e);
    toast(
      e?.response?.data?.message || "Could not load bank formats.",
      "error",
    );
  });
});
</script>

<style scoped>
.dq-template-item {
  cursor: pointer;
}
.dq-template-item.active {
  background: var(--bs-primary-bg-subtle, #eef4ff);
  color: var(--bs-body-color, #212529);
  border-color: var(--bs-primary-border-subtle, #b6d4fe);
}
.dq-template-item.active .dq-template-item__title {
  color: var(--bs-emphasis-color, #212529);
}
.dq-template-item__meta {
  color: var(--bs-secondary-color, #495057);
}
.dq-template-item.active .dq-template-item__meta {
  color: var(--bs-secondary-color, #495057);
  opacity: 1;
}
.dq-standard-desc {
  color: var(--bs-body-color, #212529);
  line-height: 1.6;
}
</style>
